import { AccredibleProfileAvailability } from '@accredible-frontend-v2/models';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { FilterService } from '../../../../services/filter.service';
import {
  SpotlightProfilesState,
  SpotlightProfilesStateAction,
} from '../../../../store/spotlight-profiles/spotlight-profiles.reducer';
import {
  selectSpotlightProfilesFilters,
  selectSpotlightProfilesStateAction,
} from '../../../../store/spotlight-profiles/spotlight-profiles.selectors';

@Component({
  selector: 'sl-profile-list-availability-filter',
  templateUrl: './profile-list-availability-filter.component.html',
  styleUrls: ['./profile-list-availability-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileListAvailabilityFilterComponent implements OnInit {
  private readonly _spotlightProfileStore = inject(Store<SpotlightProfilesState>);
  private readonly _filter = inject(FilterService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly availabilityForm = new FormGroup({
    [AccredibleProfileAvailability.FULL_TIME]: new FormControl(false),
    [AccredibleProfileAvailability.PART_TIME]: new FormControl(false),
    [AccredibleProfileAvailability.CONTRACT]: new FormControl(false),
  });

  ngOnInit(): void {
    this._initAvailabilityFormGroup();
    this._filtersResetListener();
    this._setupOnChangesListener();
  }

  private _initAvailabilityFormGroup(): void {
    // Initialize availabilityForm
    this._spotlightProfileStore
      .select(selectSpotlightProfilesFilters)
      .pipe(
        take(1),
        filter((filters) => !!filters?.availability),
      )
      .subscribe((filters) => {
        filters.availability.forEach((availability) => {
          this.availabilityForm.controls[availability].setValue(true);
        });
      });
  }

  private _filtersResetListener(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesStateAction)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((action) => action === SpotlightProfilesStateAction.FILTERS_RESET),
      )
      .subscribe(() => {
        this.availabilityForm.reset();
      });
  }

  private _setupOnChangesListener(): void {
    this.availabilityForm.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const selectedAvailability = this._getAvailabilityArray();

      // Update url without refreshing the page
      this._filter.updateRouterQueryParams({ availability: selectedAvailability });
    });
  }

  private _getAvailabilityArray = (): AccredibleProfileAvailability[] => {
    const availabilityArray = [];

    Object.keys(this.availabilityForm.controls).forEach((control) => {
      if (this.availabilityForm.controls[control].value) {
        availabilityArray.push(control);
      }
    });

    return availabilityArray.length < 1 ? null : availabilityArray;
  };
}
